import type {
  CreateWorkflowFromWorkspacePayload,
  DeleteWorkflowPayload,
  FetchWorkflowPayload,
  PublishWorkflowPayload,
} from "ee/actions/workflowActions";
import {
  ReduxActionTypes,
  type ReduxAction,
  ReduxActionErrorTypes,
} from "ee/constants/ReduxActionConstants";
import {
  WorkflowScheduleState,
  type WorkflowMetadata,
} from "ee/constants/WorkflowConstants";
import {
  DEFAULT_WORKFLOW_COLOR,
  DEFAULT_WORKFLOW_ICON,
  DEFAULT_WORKFLOW_PREFIX,
  type Workflow,
} from "ee/constants/WorkflowConstants";

import {
  CREATE_WORKFLOW_ERROR,
  FETCH_WORKFLOW_ERROR,
} from "ee/constants/messages";
import type { ApiResponse } from "api/ApiResponses";
import { createMessage } from "ee/constants/messages";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { validateResponse } from "sagas/ErrorSagas";
import history from "utils/history";
import type {
  CreateWorkflowApiKeysResponse,
  CreateWorkflowPayload,
  CreateWorkflowSchedulePayload,
  FetchWorkflowResponse,
  FetchWorkflowRunsResponse,
  FetchWorkflowsResponse,
} from "ee/api/WorkflowApi";
import WorkflowApi from "ee/api/WorkflowApi";
import {
  getCurrentWorkflowById,
  getIsFetchingWorkflows,
  getShowWorkflowFeature,
  getWorkflowFromWorkspaceById,
  getWorkflowsList,
} from "ee/selectors/workflowSelectors";
import { getNextEntityName } from "utils/AppsmithUtils";
import { workflowEditorURL } from "ee/RouteBuilder";
import { toast } from "@appsmith/ads";
import { getCurrentWorkspaceId } from "ee/selectors/selectedWorkspaceSelectors";
import { HistoryStateFilterStates } from "ee/pages/Editor/WorkflowEditor/BottomBar/WorkflowRunHistory/helpers";
import AnalyticsUtil from "ee/utils/AnalyticsUtil";
import type { APP_MODE } from "entities/App";
import { storeActionTestPayload } from "utils/storage";
import type { WorkflowRunDetailsData } from "ee/reducers/uiReducers/workflowHistoryPaneReducer";
import { DEFAULT_EVALUATION_VERSION } from "constants/EvalConstants";
import type { WorkflowSchedulerFormDataType } from "ee/pages/Editor/WorkflowEditor/WorkflowSettingsPane/WorkflowSettings/TriggerSettings/CRON/types";

interface CreateWorkflowSagaProps {
  workspaceId: string;
  name?: string;
  icon?: string;
  color?: string;
}

/**
 * Saga creates a workflow and specifically should be called from workspace
 */
export function* createWorkflowFromWorkspaceSaga(
  action: ReduxAction<CreateWorkflowFromWorkspacePayload>,
) {
  try {
    const { workspaceId } = action.payload;
    const isFetchingWorkflowsList: boolean = yield select(
      getIsFetchingWorkflows,
    );

    if (isFetchingWorkflowsList) return;

    const response: ApiResponse<Workflow> = yield call(createWorkflowSaga, {
      workspaceId,
    });
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      const workflowId = response.data.id;

      yield put({
        type: ReduxActionTypes.CREATE_WORKFLOW_FROM_WORKSPACE_SUCCESS,
        payload: response.data,
      });

      AnalyticsUtil.logEvent("CREATE_WORKFLOW", { workflowId });
      history.push(workflowEditorURL({ workflowId }));
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.CREATE_WORKFLOW_FROM_WORKSPACE_ERROR,
      payload: {
        error: {
          message: createMessage(CREATE_WORKFLOW_ERROR),
        },
      },
    });
  }
}

/**
 * Creates a workflow based on the workspaceId provided. name, icon and color are optional, so if
 * they are not provided; the saga will auto generate them.
 * For name, the saga will will look into existing workflows in the workspace and generate the next
 * possible name.
 *
 * @param payload - createWorkflowSaga
 *  {
      workspaceId: string;
      name?: string;
      icon?: string;
      color?: string;
    }
 * @returns
 */
export function* createWorkflowSaga(payload: CreateWorkflowSagaProps) {
  try {
    const workflowList: WorkflowMetadata[] = yield select(getWorkflowsList);

    const name = (() => {
      if (payload.name) return payload.name;

      const currentWorkspaceWorkflows = workflowList
        .filter(({ workspaceId }) => workspaceId === payload.workspaceId)
        .map(({ name }) => name);

      return getNextEntityName(
        DEFAULT_WORKFLOW_PREFIX,
        currentWorkspaceWorkflows,
      );
    })();

    const body: CreateWorkflowPayload = {
      workspaceId: payload.workspaceId,
      name,
      icon: payload.icon || DEFAULT_WORKFLOW_ICON,
      color: payload.color || DEFAULT_WORKFLOW_COLOR,
    };

    const response: ApiResponse = yield call(WorkflowApi.createWorkflow, body);

    return response;
  } catch (error) {
    throw error;
  }
}

export function* fetchWorkflowSaga(payload: FetchWorkflowPayload) {
  try {
    const response: FetchWorkflowResponse = yield call(
      WorkflowApi.fetchWorkflowById,
      payload,
    );
    const isValidResponse: boolean = yield call(validateResponse, response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_WORKFLOW_SUCCESS,
        payload: response,
      });

      yield put({
        type: ReduxActionTypes.SET_APP_VERSION_ON_WORKER,
        payload: response.data?.evaluationVersion || DEFAULT_EVALUATION_VERSION,
      });
    }

    return response;
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_WORKFLOW_ERROR,
      payload: {
        error: {
          message: createMessage(FETCH_WORKFLOW_ERROR),
        },
      },
    });
  }
}

export function* fetchWorkflowsForWorkspaceSaga(action?: ReduxAction<string>) {
  try {
    const showWorkflowFeature: boolean = yield select(getShowWorkflowFeature);

    if (!showWorkflowFeature) return;

    let activeWorkspaceId: string = "";

    if (!action?.payload) {
      activeWorkspaceId = yield select(getCurrentWorkspaceId);
    } else {
      activeWorkspaceId = action.payload;
    }

    const response: FetchWorkflowsResponse = yield call(
      WorkflowApi.fetchWorkflows,
      { workspaceId: activeWorkspaceId },
    );

    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_ALL_WORKFLOWS_FOR_WORKSPACE_SUCCESS,
        payload: response.data,
      });
    } else {
      yield put({
        type: ReduxActionErrorTypes.FETCH_ALL_WORKFLOWS_FOR_WORKSPACE_ERROR,
        payload: {
          error: {
            message: createMessage(FETCH_WORKFLOW_ERROR),
          },
        },
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_ALL_WORKFLOWS_FOR_WORKSPACE_ERROR,
      payload: {
        error: {
          message: createMessage(FETCH_WORKFLOW_ERROR),
        },
      },
    });
  }
}

export function* deleteWorkflowSaga(
  action: ReduxAction<DeleteWorkflowPayload>,
) {
  try {
    const response: ApiResponse<Workflow> = yield call(
      WorkflowApi.deleteWorkflow,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.DELETE_WORKFLOW_SUCCESS,
        payload: action.payload,
      });
      AnalyticsUtil.logEvent("DELETE_WORKFLOW", {
        workflowId: action.payload.id,
      });

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.DELETE_WORKFLOW_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* updateWorkflowNameSaga(
  action: ReduxAction<Partial<Workflow> & { fromWorkspace: boolean }>,
) {
  try {
    const { fromWorkspace, id: workflowId } = action.payload;
    let workflow: WorkflowMetadata;

    if (fromWorkspace) {
      workflow = yield select(getWorkflowFromWorkspaceById, workflowId || "");
    } else {
      workflow = yield select(getCurrentWorkflowById(workflowId || ""));
    }

    const response: ApiResponse<Workflow> = yield call(
      WorkflowApi.updateWorkflow,
      { ...workflow, name: action.payload.name || "" },
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.UPDATE_WORKFLOW_NAME_SUCCESS,
        payload: response.data,
      });

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.UPDATE_WORKFLOW_NAME_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* publishWorkflowSaga(
  action: ReduxAction<PublishWorkflowPayload>,
) {
  try {
    const response: ApiResponse<PublishWorkflowPayload> = yield call(
      WorkflowApi.publishWorkflow,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.PUBLISH_WORKFLOW_SUCCESS,
        payload: response.data,
      });

      AnalyticsUtil.logEvent("DEPLOY_WORKFLOW", {
        workflowId: action.payload.workflowId,
      });

      toast.show("Workflow published successfully", { kind: "success" });

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.PUBLISH_WORKFLOW_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* toggleWorkflowTokenSaga(
  action: ReduxAction<{
    workflowId: string;
    isTokenCurrentlyGenerated: boolean;
  }>,
) {
  try {
    const { isTokenCurrentlyGenerated, workflowId } = action.payload;
    let response: CreateWorkflowApiKeysResponse;

    if (!isTokenCurrentlyGenerated) {
      response = yield call(WorkflowApi.createWorkflowApiKey, workflowId);
    } else {
      response = yield call(WorkflowApi.archiveWorkflowApiKey, workflowId);
    }

    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      const type = isTokenCurrentlyGenerated
        ? ReduxActionTypes.DELETE_WORKFLOW_TOKEN_SUCCESS
        : ReduxActionTypes.CREATE_WORKFLOW_TOKEN_SUCCESS;

      yield put({
        type,
        payload: { token: response.data, workflowId },
      });

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.TOGGLE_WORKFLOW_TOKEN_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* fetchWorkflowRunHistorySaga(
  action: ReduxAction<{
    workflowId: string;
    filter: string;
    workflowMode: APP_MODE;
  }>,
) {
  try {
    const { filter, workflowId, workflowMode } = action.payload;

    const statusFilter =
      filter === HistoryStateFilterStates.ALL_RUNS ? "" : filter;
    let paramString = `?workflow-mode=${workflowMode}`;

    if (statusFilter.length > 0) {
      paramString = `${paramString}&status=${statusFilter}`;
    }

    const response: ApiResponse<FetchWorkflowRunsResponse> = yield call(
      WorkflowApi.fetchWorkflowRuns,
      workflowId,
      paramString,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      const { data } = response;

      yield put({
        type: ReduxActionTypes.FETCH_WORKFLOW_RUN_HISTORY_SUCCESS,
        payload: {
          workflowId,
          historyData: data.runs,
        },
      });
    } else {
      yield put({
        type: ReduxActionErrorTypes.FETCH_WORKFLOW_RUN_HISTORY_ERROR,
        payload: {
          error: response.responseMeta.error,
        },
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_WORKFLOW_RUN_HISTORY_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* fetchWorkflowRunHistoryDetailsSaga(
  action: ReduxAction<{
    workflowId: string;
    runId: string;
    workflowMode: APP_MODE;
  }>,
) {
  try {
    const { runId, workflowId, workflowMode } = action.payload;

    const response: ApiResponse<WorkflowRunDetailsData> = yield call(
      WorkflowApi.fetchWorkflowRunDetails,
      workflowId,
      runId,
      workflowMode,
    );
    // const isValidResponse: boolean = yield validateResponse(response);
    const isValidResponse = true;

    if (isValidResponse) {
      const { data } = response;

      yield put({
        type: ReduxActionTypes.FETCH_WORKFLOW_RUN_HISTORY_DETAILS_SUCCESS,
        payload: {
          [runId]: data,
        },
      });
    } else {
      yield put({
        type: ReduxActionErrorTypes.FETCH_WORKFLOW_RUN_HISTORY_DETAILS_ERROR,
        payload: {
          error: response.responseMeta.error,
        },
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_WORKFLOW_RUN_HISTORY_DETAILS_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* triggerTestWorkflowSaga(
  action: ReduxAction<{
    actionId: string;
    workflowId: string;
    // TODO: Fix this the next time the file is edited
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
  }>,
) {
  try {
    const { actionId, data, workflowId } = action.payload;

    yield storeActionTestPayload({ actionId, testData: data });
    const response: ApiResponse = yield call(
      WorkflowApi.triggerWorkflowTest,
      workflowId,
      data,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.TRIGGER_TEST_WORKFLOW_SUCCESS,
        payload: response.data,
      });

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.TRIGGER_TEST_WORKFLOW_ERROR,
    });
  }
}
export function* saveWorkflowSchedule(
  action: ReduxAction<{
    workflowId: string;
    scheduleId: string;
    formData: WorkflowSchedulerFormDataType;
  }>,
) {
  try {
    const { formData, scheduleId, workflowId } = action.payload;
    const isFirstTimeSave = scheduleId.length === 0;
    const {
      timezone: timeZoneName,
      workflowScheduleDOM,
      workflowScheduleDOW,
      workflowScheduleHour,
      workflowScheduleMinute,
      workflowScheduleMonth,
    } = formData;
    const cronString = `${workflowScheduleMinute} ${workflowScheduleHour} ${workflowScheduleDOM} ${workflowScheduleMonth} ${workflowScheduleDOW}`;

    const payload: CreateWorkflowSchedulePayload = {
      scheduleSpec: {
        cronExpressions: [cronString],
        timeZoneName,
      },
      triggerData: {},
      name: "Untitled schedule 1",
      state: WorkflowScheduleState.INACTIVE,
    };
    let response: Workflow;

    if (isFirstTimeSave) {
      response = yield call(
        WorkflowApi.createWorkflowSchedule,
        workflowId,
        payload,
      );
    } else {
      response = yield call(
        WorkflowApi.updateWorkflowSchedule,
        workflowId,
        scheduleId,
        payload,
      );
    }

    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_WORKFLOW_SUCCESS,
        payload: response,
      });

      toast.show(`Schedule ${isFirstTimeSave ? "created" : "updated"}`, {
        kind: "success",
      });

      return response;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.SAVE_WORKFLOW_SCHEDULE_ERROR,
      payload: {
        error,
      },
    });
  }
}
export function* toggleWorkflowSchedule(
  action: ReduxAction<{
    workflowId: string;
    scheduleId: string;
    newState: WorkflowScheduleState;
  }>,
) {
  try {
    const { newState, scheduleId, workflowId } = action.payload;
    const response: Workflow = yield call(
      WorkflowApi.toggleScheduleState,
      workflowId,
      scheduleId,
      newState,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_WORKFLOW_SUCCESS,
        payload: response,
      });
      toast.show(
        `Schedule ${newState === WorkflowScheduleState.ACTIVE ? "enabled" : "disabled"}`,
        {
          kind: "success",
        },
      );

      return response;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.SAVE_WORKFLOW_SCHEDULE_ERROR,
      payload: {
        error,
      },
    });
  }
}

export default function* workflowsSagas() {
  yield all([
    takeLatest(
      ReduxActionTypes.FETCH_ALL_WORKFLOWS_FOR_WORKSPACE_INIT,
      fetchWorkflowsForWorkspaceSaga,
    ),
    takeLatest(
      ReduxActionTypes.CREATE_WORKFLOW_FROM_WORKSPACE_INIT,
      createWorkflowFromWorkspaceSaga,
    ),
    takeLatest(ReduxActionTypes.DELETE_WORKFLOW_INIT, deleteWorkflowSaga),
    takeLatest(
      ReduxActionTypes.UPDATE_WORKFLOW_NAME_INIT,
      updateWorkflowNameSaga,
    ),
    takeLatest(ReduxActionTypes.PUBLISH_WORKFLOW_INIT, publishWorkflowSaga),
    takeLatest(ReduxActionTypes.TOGGLE_WORKFLOW_TOKEN, toggleWorkflowTokenSaga),
    takeLatest(
      ReduxActionTypes.FETCH_WORKFLOW_RUN_HISTORY_INIT,
      fetchWorkflowRunHistorySaga,
    ),
    takeLatest(
      ReduxActionTypes.FETCH_WORKFLOW_RUN_HISTORY_DETAILS_INIT,
      fetchWorkflowRunHistoryDetailsSaga,
    ),
    takeLatest(
      ReduxActionTypes.TRIGGER_TEST_WORKFLOW_INIT,
      triggerTestWorkflowSaga,
    ),
    takeLatest(ReduxActionTypes.WORKFLOW_SAVE_SCHEDULE, saveWorkflowSchedule),
    takeLatest(
      ReduxActionTypes.WORKFLOW_TOGGLE_SCHEDULE,
      toggleWorkflowSchedule,
    ),
  ]);
}
